import React from "react";
import { useContext, useEffect, useState } from "react";

import MainContext from "../../../Context/MainContext/MainContext";
import MUIButton from "@mui/material/Button";
import MUIAddIcon from "@mui/icons-material/Add";
import MUITable from "@mui/material/Table";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITableBody from "@mui/material/TableBody";
import MUITypography from "@mui/material/Typography";
import MUIStack from "@mui/material/Stack";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIToolTip from "@mui/material/Tooltip";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIExpandLessIcon from "@mui/icons-material/ExpandLess";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MUICollapse from "@mui/material/Collapse";
import MUIBox from "@mui/material/Box";
import MUITablePagination from "@mui/material/TablePagination";
import * as MUIIcons from "@mui/icons-material";

import OrderActivityType from "../../../Utility/Crud/OrderActivityType";
import Card from "../../Card";
import Menu from "../../Menu";
import Format from "../../../Utility/Format";
import EmptyState from "../../EmptyState";

import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Patient from "../../../Utility/Crud/Patient";
import ChipOrderStatusReason from "../../Chip/ChipOrderStatusReason";
import ChipOrderStatus from "../../Chip/ChipOrderStatus";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";

/**
 * CardChartEquipment renders a Card component
 * displaying patient orders and tickets data.
 * @returns {JSX.Element}
 */
const className = "CardChartPatientEquipment";
const title = "Patient Equipment";
const icon = MUIAddIcon;

export default function CardChartPatientEquipment() {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const [ordersAndTickets, setOrdersAndTickets] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (expandedOrder && Object.keys(chartContext.expandedRows).length === 0) {
      chartContext.updateExpandedRow(expandedOrder.id);
    }
  }, [expandedOrder]);

  /**
   * Use effect to set the orders and tickets.
   */
  useEffect(() => {
    const orderActivityMap = {};

    const buildBaseOrder = (order, activity, products) => {
      const primaryProduct = OrderActivity.getPrimaryProduct(products);

      return {
        ...order,
        tickets: [],
        activity: activity,
        orderActivityProducts: products || [],
        primaryProduct: primaryProduct,
      };
    };

    for (const orderActivity of Object.values(
      chartContext.chart.orderActivity,
    )) {
      const serviceReason = OrderActivity.getServiceOrderReason(
        orderActivity,
        chartContext.chart,
      );
      const activity = chartContext.chart.activity[orderActivity.id];
      const products = OrderActivity.getProducts(
        orderActivity,
        chartContext.chart,
      );
      const primaryProduct = OrderActivity.getPrimaryProduct(products);

      const hasParentLink = !!serviceReason?.previousOrderId;
      if (!hasParentLink) {
        if (!orderActivityMap[orderActivity.id]) {
          const baseOrder = buildBaseOrder(orderActivity, activity, products);
          orderActivityMap[orderActivity.id] = baseOrder;
          orderActivityMap[orderActivity.id].tickets.push({
            ...orderActivity,
            activity: activity,
            parentOrderId: orderActivity.id,
            primaryProduct: primaryProduct,
          });
        }
      } else {
        const originalParent =
          OrderActivity.getParentOrder(
            serviceReason.previousOrderId,
            chartContext.chart,
          ) ||
          chartContext.chart.orderActivity?.[serviceReason.previousOrderId];
        const parentId = originalParent?.id;

        if (!orderActivityMap[parentId] && originalParent) {
          const parentActivity = chartContext.chart.activity[originalParent.id];
          const parentProducts = OrderActivity.getProducts(
            originalParent,
            chartContext.chart,
          );
          const parentBaseOrder = buildBaseOrder(
            originalParent,
            parentActivity,
            parentProducts,
          );
          orderActivityMap[parentId] = parentBaseOrder;
          orderActivityMap[parentId].tickets.push({
            ...originalParent,
            activity: parentActivity,
            parentOrderId: parentId,
            primaryProduct: parentBaseOrder.primaryProduct,
          });
        }

        if (orderActivityMap[parentId]) {
          const ticketProducts = OrderActivity.getProducts(
            orderActivity,
            chartContext.chart,
          );
          const ticketPrimaryProduct =
            OrderActivity.getPrimaryProduct(ticketProducts);

          const serviceQuotes = OrderActivity.getServiceQuotes(
            orderActivity,
            chartContext.chart,
          );
          const servicePartCatalog = OrderActivity.getServicePartCatalog(
            serviceQuotes,
            chartContext.chart,
          );

          orderActivityMap[parentId].tickets.push({
            ...orderActivity,
            activity: activity,
            parentOrderId: parentId,
            primaryProduct: ticketPrimaryProduct,
            orderActivityProducts: ticketProducts || [],
            serviceQuote: serviceQuotes,
            servicePartCatalog: servicePartCatalog,
          });
        }
      }
    }

    const sortedOrders = OrderActivity.sort(Object.values(orderActivityMap));

    sortedOrders.forEach((order) => {
      order.tickets.sort((a, b) => {
        if (a.id === order.id) {
          return 1;
        }
        if (b.id === order.id) {
          return -1;
        }

        // Sort cancelled tickets above order ticket but below others
        if (a.status === 4 && b.status !== 4) {
          return 1;
        }
        if (a.status !== 4 && b.status === 4) {
          return -1;
        }

        const aDate = a.activity.startedAt;
        const bDate = b.activity.startedAt;
        return new Date(bDate) - new Date(aDate);
      });
    });

    setOrdersAndTickets(sortedOrders);
    setExpandedOrder(sortedOrders[0]);
  }, [chartContext.chart.orderActivity]);

  const createComplaintFeedbackDraft = (order, issueReasonId) => {
    const currentPatientIssues = Object.values(
      chartContext.chart.patientIssue || {},
    );
    const draftIssues = currentPatientIssues
      .filter(
        (issue) => issue.id < 0 && issue.patientId === chartContext.patientId,
      )
      .sort((a, b) => a.id - b.id);

    const primaryDraftId = draftIssues[0]?.id || chartContext.getNegativeId();
    const draftIssueIdsToDelete = draftIssues
      .filter((issue) => issue.id !== primaryDraftId)
      .map((issue) => issue.id);

    const patientIssueUpdates = {};
    draftIssueIdsToDelete.forEach((draftIssueId) => {
      patientIssueUpdates[draftIssueId] = undefined;
    });

    const patientIssueNoteUpdates = {};
    for (const note of Object.values(
      chartContext.chart.patientIssueNote || {},
    )) {
      if (
        note.id < 0 ||
        note.patientIssueId === primaryDraftId ||
        draftIssueIdsToDelete.includes(note.patientIssueId)
      ) {
        patientIssueNoteUpdates[note.id] = undefined;
      }
    }

    patientIssueUpdates[primaryDraftId] = {
      id: primaryDraftId,
      patientId: chartContext.patientId,
      issueLoggedById: mainContext.currentUser.id,
      issueLoggedOn: new Date().toISOString(),
      issueStatus: 0,
      issueCategoryId: 2,
      issueReasonId: issueReasonId,
      orderId: order.id,
      complaintDescription: null,
      assignedToId: null,
      complimentedUserId: null,
      nextFollowupDate: null,
    };

    const updates = { patientIssue: patientIssueUpdates };
    if (Object.keys(patientIssueNoteUpdates).length > 0) {
      updates.patientIssueNote = patientIssueNoteUpdates;
    }
    chartContext.onChange(updates);
    chartContext.requestScrollToCard({
      cardName: "CardChartFeedback",
      columnName: "right",
    });
  };

  const renderTicketsRow = (ticket) => {
    const renderOrderStatusReasonChip = (ticket) => {
      if (!ticket.orderStatusReason && ticket.status === 4) {
        return (
          <ChipOrderStatus
            orderStatus={mainContext.orderStatusesIndexed[ticket.status]}
          />
        );
      } else {
        return (
          <ChipOrderStatusReason
            orderStatusReason={
              mainContext.orderStatusReasonsIndexed[ticket.orderStatusReasonId]
            }
          />
        );
      }
    };

    const serviceReason = OrderActivity.getServiceOrderReason(
      ticket,
      chartContext.chart,
    )?.serviceReason;

    const getPointsOrParts = (ticket, delimeter = ", ") => {
      if (ticket.orderType === "New") {
        return OrderActivity.getApprovedPoints(
          OrderActivity.getProducts(ticket, chartContext.chart),
        ).toFixed(2);
      } else if (ticket.servicePartCatalog?.length) {
        return ticket.servicePartCatalog
          .map((item) => item.description)
          .join(delimeter);
      } else {
        return "None";
      }
    };

    const getLastNoteOrDeliveredDate = (ticket) => {
      if (ticket.status === 3) {
        return Format.date(ticket?.setupDate) || "N/A";
      } else {
        return Format.date(ticket.activity?.lastNoteDate) || "N/A";
      }
    };
    const getDateHeader = (ticket) => {
      if (ticket.orderType === "New" && ticket.status === 3) {
        return "Delivered";
      } else if (ticket.status === 3) {
        return "Completed";
      } else {
        return "Last Note";
      }
    };

    return (
      <MUITableRow
        key={ticket.id}
        onClick={() => {
          chartContext.handleNavigateChart(
            `/chart/${chartContext.patientId}/order/${ticket.id}`,
          );
        }}
        sx={{
          cursor: "pointer",
          "& td": {
            borderBottom: "none",
            py: "5px",
          },
          "&:hover": {
            backgroundColor: (theme) => theme.palette.background.default,
          },
        }}
      >
        <MUITableCell style={{ paddingLeft: "2rem", width: "200px" }}>
          <MUIStack direction="column" spacing={0}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                whiteSpace: "nowrap",
              }}
            >
              {React.createElement(
                MUIIcons[OrderActivityType.getIconName(ticket?.orderType)],
                {
                  fontSize: "small",
                  sx: { color: (theme) => theme.palette.primary.main },
                },
              )}
              {ticket.orderType}
            </span>
            {serviceReason && (
              <MUITypography
                variant="caption"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                {serviceReason}
              </MUITypography>
            )}
          </MUIStack>
        </MUITableCell>
        <MUITableCell sx={{ width: "100px" }}>{`#${ticket.id}`}</MUITableCell>
        <MUITableCell sx={{ width: "300px" }}>
          {renderOrderStatusReasonChip(ticket)}
        </MUITableCell>
        <MUITableCell sx={{ width: "130px" }}>
          <MUIStack direction="column" spacing={0}>
            <MUITypography variant="subtitle2">
              {getDateHeader(ticket)}
            </MUITypography>
            <MUITypography variant="body2" sx={{ whiteSpace: "nowrap" }}>
              {getLastNoteOrDeliveredDate(ticket)}
            </MUITypography>
          </MUIStack>
        </MUITableCell>
        <MUITableCell>
          <MUIStack direction="column" spacing={0}>
            <MUITypography variant="subtitle2">
              {ticket.orderType === "New" ? "Points" : "Equipment"}
            </MUITypography>
            <MUITypography
              variant="body2"
              noWrap
              sx={{ maxWidth: 350 }}
              title={getPointsOrParts(ticket, "\n")}
            >
              {getPointsOrParts(ticket)}
            </MUITypography>
          </MUIStack>
        </MUITableCell>
      </MUITableRow>
    );
  };

  /**
   * Renders the other equipment section.
   * @returns {JSX.Element} The other equipment section.
   */
  const renderEquipment = () => {
    const paginatedOrders = ordersAndTickets.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    const patientDeniedServiceIssues = Patient.getServiceDeniedReasons(
      chartContext.chart.patient[chartContext.patientId],
      chartContext.chart.patientIssue,
      mainContext.issueReasonsIndexed,
    );
    return (
      ordersAndTickets.length > 0 && (
        <MUIBox sx={{ overflowX: "auto" }}>
          <MUITable size="small">
            <MUITableBody>
              {paginatedOrders.map((order) => {
                const isExpanded = chartContext.expandedRows[order.id] || false;
                const completedPickupTicketOrderActivityId =
                  OrderActivity.getCompletedPickupTicketOrderActivityId(
                    order.id,
                    chartContext.chart,
                    mainContext.orderStatusesIndexed,
                  );
                const completedPickupTicket =
                  completedPickupTicketOrderActivityId
                    ? chartContext.chart.orderActivity[
                        completedPickupTicketOrderActivityId
                      ]
                    : null;
                const disableServiceTicketCreation =
                  order.status !== 3 ||
                  !!completedPickupTicketOrderActivityId ||
                  (patientDeniedServiceIssues &&
                    patientDeniedServiceIssues.length > 0);
                const disableBillingIssueTicketCreation = order.status !== 3;
                const disablePickupTicketCreation =
                  !!completedPickupTicketOrderActivityId ||
                  order.status === 4 ||
                  (order.orderType?.toLowerCase() === "new" &&
                    order.status !== 3);
                return (
                  <React.Fragment key={order.id}>
                    <MUITableRow
                      onClick={() => chartContext.updateExpandedRow(order.id)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.background.default,
                        },
                      }}
                    >
                      <MUITableCell sx={{ width: "100%" }}>
                        <MUIStack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ width: 'min-content "!important"' }}
                        >
                          {chartContext.expandedRows[order.id] ? (
                            <MUIExpandLessIcon
                              fontSize="small"
                              sx={{
                                color: (theme) => theme.palette.text.disabled,
                              }}
                            />
                          ) : (
                            <MUIExpandMoreIcon
                              fontSize="small"
                              sx={{
                                color: (theme) => theme.palette.text.disabled,
                              }}
                            />
                          )}
                          <MUITypography variant="subtitle2">
                            {order.primaryProduct
                              ? OrderActivity.getProductDisplayName(
                                  order.primaryProduct,
                                  mainContext.baseUnitsIndexed,
                                  mainContext.vendorsIndexed,
                                  mainContext.productSubTypesIndexed,
                                ) + " "
                              : "No Equipment Added"}
                            <span
                              style={{
                                fontWeight: "normal",
                                marginLeft: "0.5rem",
                              }}
                            >
                              {order.primaryProduct?.serial || ""}
                            </span>
                            {" " || "No Equipment"}
                          </MUITypography>
                        </MUIStack>
                      </MUITableCell>
                      <MUITableCell>{`#${order.id}`}</MUITableCell>
                      <MUITableCell
                        align="center"
                        sx={{ width: "1%", px: 0.5 }}
                      >
                        {completedPickupTicket && (
                          <MUIToolTip
                            title={`Pickup completed ${Format.date(completedPickupTicket.setupDate || completedPickupTicket.lastStatusChange)}`}
                            placement="top"
                            arrow
                          >
                            {React.createElement(
                              MUIIcons[OrderActivityType.getIconName("Pickup")],
                              {
                                fontSize: "small",
                                sx: {
                                  color: (theme) => theme.palette.primary.main,
                                },
                              },
                            )}
                          </MUIToolTip>
                        )}
                      </MUITableCell>
                      <MUITableCell align="right">
                        <ChipOrderStatus
                          orderStatus={
                            order.status === 3
                              ? {
                                  name: `${order?.setupDate ? "Delivered " + Format.date(order.setupDate) : "Delivered"}`,
                                  colorId: 132,
                                }
                              : mainContext.orderStatusesIndexed[order.status]
                          }
                        />
                      </MUITableCell>
                      <MUITableCell
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Menu
                          menuItems={[
                            <MUIToolTip
                              key="create-service-ticket-tooltip"
                              title={
                                patientDeniedServiceIssues &&
                                patientDeniedServiceIssues.length > 0
                                  ? patientDeniedServiceIssues.map(
                                      (issue, index) => (
                                        <React.Fragment key={index}>
                                          {issue}
                                          <br />
                                        </React.Fragment>
                                      ),
                                    )
                                  : ""
                              }
                              placement="right"
                              arrow
                            >
                              <MUIMenuItem
                                onClick={() =>
                                  chartContext.prepareTicketData(
                                    order,
                                    "service",
                                  )
                                }
                                disabled={disableServiceTicketCreation}
                              >
                                <MUIListItemIcon>
                                  {React.createElement(
                                    MUIIcons[
                                      OrderActivityType.getIconName("Service")
                                    ],
                                    {
                                      fontSize: "small",
                                    },
                                  )}
                                </MUIListItemIcon>
                                Service Ticket
                              </MUIMenuItem>
                            </MUIToolTip>,
                            <MUIMenuItem
                              onClick={() =>
                                chartContext.prepareOrderData(
                                  "accessoryAfterSale",
                                  order,
                                )
                              }
                              disabled={disableServiceTicketCreation}
                            >
                              <MUIListItemIcon>
                                {React.createElement(
                                  MUIIcons[
                                    OrderActivityType.getIconName(
                                      "Accessory After Sale",
                                    )
                                  ],
                                  {
                                    fontSize: "small",
                                  },
                                )}
                              </MUIListItemIcon>
                              Accessory After Sale
                            </MUIMenuItem>,
                            <MUIMenuItem
                              onClick={() =>
                                chartContext.prepareTicketData(order, "pickup")
                              }
                              disabled={disablePickupTicketCreation}
                            >
                              <MUIListItemIcon>
                                {React.createElement(
                                  MUIIcons[
                                    OrderActivityType.getIconName("Pickup")
                                  ],
                                  {
                                    fontSize: "small",
                                  },
                                )}
                              </MUIListItemIcon>
                              Pickup Ticket
                            </MUIMenuItem>,
                            <MUIMenuItem
                              onClick={() =>
                                chartContext.prepareTicketData(
                                  order,
                                  "billingIssue",
                                )
                              }
                              disabled={disableBillingIssueTicketCreation}
                            >
                              <MUIListItemIcon>
                                {React.createElement(
                                  MUIIcons[
                                    OrderActivityType.getIconName(
                                      "Billing Issue",
                                    )
                                  ],
                                  {
                                    fontSize: "small",
                                  },
                                )}
                              </MUIListItemIcon>
                              Billing Issue Ticket
                            </MUIMenuItem>,
                            <MUIMenuItem
                              onClick={() =>
                                createComplaintFeedbackDraft(order, 60)
                              }
                              disabled={disablePickupTicketCreation}
                            >
                              <MUIListItemIcon>
                                {React.createElement(MUIIcons.ThumbDown, {
                                  fontSize: "small",
                                })}
                              </MUIListItemIcon>
                              Pickup - Exchange
                            </MUIMenuItem>,
                            <MUIMenuItem
                              onClick={() =>
                                createComplaintFeedbackDraft(order, 61)
                              }
                              disabled={disablePickupTicketCreation}
                            >
                              <MUIListItemIcon>
                                {React.createElement(MUIIcons.ThumbDown, {
                                  fontSize: "small",
                                })}
                              </MUIListItemIcon>
                              Pickup - 5 Day
                            </MUIMenuItem>,
                          ]}
                        />
                      </MUITableCell>
                    </MUITableRow>
                    <MUITableRow>
                      <MUITableCell
                        colSpan={12}
                        sx={{ borderBottom: "none", padding: 0 }}
                      >
                        <MUICollapse in={isExpanded}>
                          <MUITable>
                            <MUITableBody>
                              {order.tickets.map((ticket) =>
                                renderTicketsRow(ticket),
                              )}
                            </MUITableBody>
                          </MUITable>
                        </MUICollapse>
                      </MUITableCell>
                    </MUITableRow>
                  </React.Fragment>
                );
              })}
            </MUITableBody>
          </MUITable>
          {renderTablePagination()}
        </MUIBox>
      )
    );
  };

  /**
   * Renders table pagination.
   */
  const renderTablePagination = () => {
    const handleChangeRowsPerPage = (e) => {
      setRowsPerPage(e.target.value);
      setPage(0);
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    return (
      <MUITablePagination
        rowsPerPageOptions={[5]}
        component="div"
        count={ordersAndTickets.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          ".MuiTablePagination-toolbar": {
            marginTop: 2,
          },
          ".MuiTablePagination-selectLabel": {
            marginTop: "13px",
          },
          ".MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          ".MuiTablePagination-actions": {
            marginTop: "-3px",
          },
        }}
      />
    );
  };

  /**
   * Renders the content of the card.
   * @returns {JSX.Element} The content of the card.
   */
  const renderContent = () => {
    if (ordersAndTickets.length === 0) {
      return (
        <EmptyState
          line1="No equipment"
          line2="Create an order to get started"
        />
      );
    }

    return renderEquipment();
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const patientDeniedServiceIssues = Patient.getServiceDeniedReasons(
      chartContext.chart.patient[chartContext.patientId],
      chartContext.chart.patientIssue,
      mainContext.issueReasonsIndexed,
    );
    return [
      <MUIToolTip
        key="create-service-ticket-tooltip"
        title={
          patientDeniedServiceIssues && patientDeniedServiceIssues.length > 0
            ? patientDeniedServiceIssues.map((issue, index) => (
                <React.Fragment key={index}>
                  {issue}
                  <br />
                </React.Fragment>
              ))
            : ""
        }
        placement="right"
        arrow
      >
        <span>
          <MUIButton
            variant="outlined"
            disabled={
              (patientDeniedServiceIssues &&
                patientDeniedServiceIssues.length > 0) ||
              chartContext.isUpdating
            }
            startIcon={<MUIAddIcon />}
            onClick={() => chartContext.prepareOrderData("new")}
            size="small"
          >
            Create New Order
          </MUIButton>
        </span>
      </MUIToolTip>,
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
    />
  );
}

CardChartPatientEquipment.meta = {
  className: className,
  title: title,
  icon: icon,
};
